import { useDisclosure } from '@heroui/react';
import { useCallback, useMemo } from 'react';
import { FaEdit } from 'react-icons/fa';
import { FaTrash } from 'react-icons/fa6';
import { Button, Confirm, cn, toaster } from '@/core/ui';
import { DeletePromptsDocument, type PromptFieldsFragment, PromptsDocument } from '@/graphql/gql/graphql';
import '@/graphql/mutation/prompt';
import { parsePromptNumericId } from '@/app/(dashboard)/prompts/constants';
import { useMutation } from '@/libs';
import { usePrompt } from '@/store/prompt';

export interface PromptItemActionsProps {
  promptId: string | null | undefined;
  name: string;
  prompt?: PromptFieldsFragment | null;
}

export function PromptItemActions({ promptId, prompt, name }: PromptItemActionsProps) {
  const openPromptEditor = usePrompt.use.openPromptEditor();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [deletePrompts, { loading: deleteLoading }] = useMutation(DeletePromptsDocument, {
    refetchQueries: [PromptsDocument],
  });

  const numericId = useMemo(() => parsePromptNumericId(promptId), [promptId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (numericId == null) return;
    await toaster.toast.promise(
      async () => {
        const res = await deletePrompts({ variables: { ids: [numericId] } });
        if (!res.data?.admin?.deletePrompts) {
          throw new Error('Failed to delete the prompt');
        }
      },
      {
        loading: 'Deleting prompt…',
        success: 'Prompt deleted',
        error: (err: Error) => err.message,
      },
      { position: 'top-center' },
    );
  }, [deletePrompts, numericId]);

  if (!promptId) {
    return null;
  }

  return (
    <>
      <Confirm
        type='danger'
        backdrop='opaque'
        isOpen={isOpen}
        isLoading={deleteLoading}
        title='Delete prompt'
        message={
          <>
            Are you sure you want to delete <b>{name}</b>?
          </>
        }
        onOpenChange={onOpenChange}
        onConfirm={handleDeleteConfirm}
      />
      <div className='flex items-center gap-1'>
        <Button
          size='sm'
          variant='faded'
          isIconOnly
          isDisabled={!prompt}
          aria-label='Edit prompt'
          className='min-w-0 text-default-500'
          onPress={() => {
            if (prompt) {
              openPromptEditor(prompt);
            }
          }}
        >
          <FaEdit className={cn('size-3.5', 'text-default-500')} />
        </Button>
        <Button
          size='sm'
          variant='faded'
          isIconOnly
          isDisabled={numericId == null}
          aria-label='Delete prompt'
          className='min-w-0 text-danger-400'
          onPress={onOpen}
        >
          <FaTrash className={cn('size-3', 'text-danger-400')} />
        </Button>
      </div>
    </>
  );
}
